// Super-admin page for the 5 BM data-plane threat lists.
//
// Backend: feat/bm-threat-lists-admin (CP PR) ships
//   GET    /api/admin/bm/threat-lists                    summary
//   GET    /api/admin/bm/threat-lists/:name              members (SSCAN paginated)
//   POST   /api/admin/bm/threat-lists/:name/add          {value}
//   DELETE /api/admin/bm/threat-lists/:name/remove       {value}
//   POST   /api/admin/bm/threat-lists/:name/bulk-add     {values: [...]}
//
// Skips a dedicated context — every action is a one-off mutation
// against a known set of endpoints, so a useState + axios pattern
// is simpler than wiring a reducer.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

import axios from "../../../utils/axios/v1/adminAxios";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import { SectionCard, TOKENS } from "../../../components/pages/_new/super/design";
import { SnackbarAlert } from "../../../components/pages/common/styled";

const LIST_DEFS = [
  { name: "ai_agents_ua", label: "AI Agent UAs" },
  { name: "ja4_bad",      label: "Bad JA4" },
  { name: "goodbot_ips",  label: "Good-bot IPs" },
  { name: "badip",        label: "Bad IPs" },
  { name: "badasn",       label: "Bad ASNs" },
];

// Per-list placeholder shown in the Add input. Mirrors the engine's
// expected shape so operators have a hint of what's valid.
const PLACEHOLDERS = {
  ai_agents_ua: "GPTBot",
  ja4_bad:      "t13d3112h2_e8f1e7e78f70_b26ce05bbdd6",
  goodbot_ips:  "66.249.66.0/19  (or single IPv4/IPv6)",
  badip:        "1.2.3.4  (or CIDR)",
  badasn:       "13335",
};

export default function SAThreatLists() {
  const [activeName, setActiveName] = useState(LIST_DEFS[0].name);
  const [summary, setSummary] = useState({});  // name -> {count, label, description}
  const [members, setMembers] = useState({});  // name -> string[]
  const [cursor, setCursor] = useState({});    // name -> next_cursor string
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", success: true });

  const [addValue, setAddValue] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [busy, setBusy] = useState(false);

  const showSnack = (message, success = true) => setSnack({ open: true, message, success });

  const refreshSummary = async () => {
    try {
      const { data } = await axios.get("api/admin/bm/threat-lists");
      const idx = {};
      (data.lists || []).forEach((l) => { idx[l.name] = l; });
      setSummary(idx);
    } catch (e) {
      showSnack(`Summary fetch failed: ${e?.response?.data?.message || e.message}`, false);
    }
  };

  const loadMembers = async (name, { append = false } = {}) => {
    setLoading(true);
    try {
      const c = append ? (cursor[name] || "0") : "0";
      const { data } = await axios.get(`api/admin/bm/threat-lists/${name}`, {
        params: { cursor: c, limit: 200 },
      });
      setMembers((prev) => ({
        ...prev,
        [name]: append ? [...(prev[name] || []), ...(data.members || [])] : (data.members || []),
      }));
      setCursor((prev) => ({ ...prev, [name]: data.next_cursor || "0" }));
      // Refresh the per-tab count from the authoritative total returned
      // by the server (SCARD), not by counting locally — for paginated
      // lists the local array is a prefix.
      setSummary((prev) => ({
        ...prev,
        [name]: { ...(prev[name] || {}), count: data.total },
      }));
    } catch (e) {
      showSnack(`Load failed: ${e?.response?.data?.message || e.message}`, false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshSummary(); }, []);
  useEffect(() => {
    if (activeName) loadMembers(activeName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeName]);

  const handleAdd = async () => {
    const value = addValue.trim();
    if (!value) return;
    setBusy(true);
    try {
      await axios.post(`api/admin/bm/threat-lists/${activeName}/add`, { value });
      setAddValue("");
      showSnack(`Added '${value}' to ${activeName}`);
      await loadMembers(activeName);
    } catch (e) {
      showSnack(`Add failed: ${e?.response?.data?.message || e.message}`, false);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (value) => {
    if (!window.confirm(`Remove '${value}' from ${activeName}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`api/admin/bm/threat-lists/${activeName}/remove`, {
        data: { value },
      });
      showSnack(`Removed '${value}'`);
      await loadMembers(activeName);
    } catch (e) {
      showSnack(`Remove failed: ${e?.response?.data?.message || e.message}`, false);
    } finally {
      setBusy(false);
    }
  };

  const handleBulkAdd = async () => {
    // Accept any whitespace separator (newline, comma, tab) so operators
    // can paste from spreadsheets or quoted-array dumps. Strip quotes /
    // brackets too.
    const cleaned = bulkText
      .replace(/[[\]"]/g, "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (cleaned.length === 0) {
      showSnack("Nothing to add — paste one entry per line", false);
      return;
    }
    if (cleaned.length > 1000) {
      showSnack(`Capped at 1000 per request (got ${cleaned.length})`, false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`api/admin/bm/threat-lists/${activeName}/bulk-add`, {
        values: cleaned,
      });
      showSnack(`Added ${data.added}; skipped ${data.skipped_duplicates || 0} duplicate(s)`);
      setBulkText("");
      setBulkOpen(false);
      await loadMembers(activeName);
    } catch (e) {
      showSnack(`Bulk add failed: ${e?.response?.data?.message || e.message}`, false);
    } finally {
      setBusy(false);
    }
  };

  const activeSummary = summary[activeName] || {};
  const activeMembers = members[activeName] || [];
  const hasMore = cursor[activeName] && cursor[activeName] !== "0";

  return (
    <>
      <Helmet title="BM Threat Lists" />
      <Stack spacing={4} sx={{ p: 6 }}>
        <PageHeader
          title="BM Threat Lists"
          description="Operator-curated lists read directly by the BM-Engine and pingora on every request."
        />

        <SectionCard>
          <Tabs
            value={activeName}
            onChange={(_e, v) => setActiveName(v)}
            variant="scrollable"
            sx={{ borderBottom: `1px solid ${TOKENS?.colors?.border || "#E5E7EB"}` }}
          >
            {LIST_DEFS.map(({ name, label }) => (
              <Tab
                key={name}
                value={name}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{label}</Typography>
                    {summary[name]?.count !== undefined && (
                      <Chip size="small" label={summary[name].count} sx={{ height: 18, fontSize: 11 }} />
                    )}
                  </Stack>
                }
              />
            ))}
          </Tabs>

          <Box sx={{ pt: 3 }}>
            <Typography sx={{ fontSize: 13, color: TOKENS?.colors?.muted || "#6B7280", mb: 2 }}>
              <strong>{activeSummary.label || activeName}</strong> — Redis key <code>{activeSummary.key || ""}</code>
            </Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS?.colors?.muted || "#6B7280", mb: 3 }}>
              {activeSummary.description || ""}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={PLACEHOLDERS[activeName] || ""}
                value={addValue}
                onChange={(e) => setAddValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !busy) handleAdd(); }}
                disabled={busy}
              />
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={busy || !addValue.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
              <Button
                variant="outlined"
                onClick={() => setBulkOpen(true)}
                disabled={busy}
              >
                Bulk add…
              </Button>
            </Stack>

            {loading && activeMembers.length === 0 ? (
              <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                    <TableCell sx={{ width: 80, fontWeight: 600 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography sx={{ py: 3, textAlign: "center", color: TOKENS?.colors?.muted || "#6B7280" }}>
                          No entries yet. Add one above or bulk-paste many.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeMembers.map((m) => (
                      <TableRow key={m} hover>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{m}</TableCell>
                        <TableCell>
                          <Tooltip title="Remove" arrow>
                            <span>
                              <IconButton size="small" onClick={() => handleRemove(m)} disabled={busy}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {hasMore && (
              <Stack alignItems="center" sx={{ mt: 3 }}>
                <Button onClick={() => loadMembers(activeName, { append: true })} disabled={loading}>
                  Load more
                </Button>
              </Stack>
            )}
          </Box>
        </SectionCard>
      </Stack>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk add to {activeSummary.label || activeName}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12, color: TOKENS?.colors?.muted || "#6B7280", mb: 2 }}>
            Paste up to 1000 entries. Whitespace, commas, brackets, and quotes are all tolerated — one entry per
            token. Duplicates and entries already present are silently skipped.
          </Typography>
          <TextField
            multiline
            minRows={10}
            maxRows={20}
            fullWidth
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={`${PLACEHOLDERS[activeName] || ""}\n${PLACEHOLDERS[activeName] || ""}\n...`}
            sx={{ fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={handleBulkAdd} variant="contained" disabled={busy}>Add all</Button>
        </DialogActions>
      </Dialog>

      <SnackbarAlert
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
        severity={snack.success ? "success" : "error"}
      />
    </>
  );
}
